import { apiClient, assertValidParam } from '@v2/lib';
import type {
  BitwerxOffering,
  BitwerxOfferingDetail,
  BitwerxOfferingsResponse,
  BitwerxOfferingDetailsApiResponse,
  CreateBitwerxOfferingPayload,
  UpdateBitwerxOfferingPayload,
  CreateOfferingPayload,
  UpdateOfferingPayload,
} from '@v2/types';

/**
 * Offerings Service
 *
 * Domain: Bitwerx offerings and partner offerings management
 *
 * Queries:
 *   - getBitwerxOfferings()          → GET /offerings/bitwerx
 *   - getBitwerxOfferingDetails(id)  → GET /offerings/bitwerx/:id
 *
 * Mutations:
 *   - createBitwerxOffering(payload) → POST /offerings/bitwerx
 *   - updateBitwerxOffering(id, payload) → PATCH /offerings/bitwerx/:id
 *   - removeBitwerxOffering(id)      → DELETE /offerings/bitwerx/:id
 *   - removeBitwerxSubOffering(id)   → DELETE /offerings/bitwerx/suboffering/:id
 *   - createPartnerOffering(partnerId, payload) → POST /offerings/partner/:partnerId
 *   - updatePartnerOffering(partnerId, payload) → PATCH /offerings/partner/:partnerId
 */

export const offeringsService = {
  getBitwerxOfferings: async (): Promise<BitwerxOffering[]> => {
    const response = await apiClient().get<BitwerxOfferingsResponse>('offerings/bitwerx');
    return response.data?.items ?? [];
  },

  getBitwerxOfferingDetails: async (id: string): Promise<BitwerxOfferingDetail> => {
    const safeId = assertValidParam(id, 'offeringId');
    const response = await apiClient().get<BitwerxOfferingDetailsApiResponse>(
      `offerings/bitwerx/${safeId}`,
    );
    return response.data.data;
  },

  createBitwerxOffering: async (payload: CreateBitwerxOfferingPayload): Promise<unknown> => {
    const response = await apiClient().post('offerings/bitwerx', payload);
    return response.data;
  },

  updateBitwerxOffering: async (
    id: string,
    payload: UpdateBitwerxOfferingPayload,
  ): Promise<unknown> => {
    const safeId = assertValidParam(id, 'offeringId');
    const response = await apiClient().patch(`offerings/bitwerx/${safeId}`, payload);
    return response.data;
  },

  removeBitwerxOffering: async (id: string): Promise<string> => {
    const safeId = assertValidParam(id, 'offeringId');
    const response = await apiClient().delete(`offerings/bitwerx/${safeId}`);
    return typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
  },

  removeBitwerxSubOffering: async (subOfferingId: string): Promise<string> => {
    const safeId = assertValidParam(subOfferingId, 'subOfferingId');
    const response = await apiClient().delete(`offerings/bitwerx/suboffering/${safeId}`);
    return typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
  },

  createPartnerOffering: async (
    partnerId: string,
    payload: CreateOfferingPayload,
  ): Promise<void> => {
    const safeId = assertValidParam(partnerId, 'partnerId');
    await apiClient().post(`offerings/partner/${safeId}`, payload);
  },

  updatePartnerOffering: async (
    partnerId: string,
    payload: UpdateOfferingPayload,
  ): Promise<void> => {
    const safeId = assertValidParam(partnerId, 'partnerId');
    await apiClient().patch(`offerings/partner/${safeId}`, payload);
  },
};
